import { z } from "zod";

import { CallScriptSchema } from "./schemas/CallScript";

// Define the CallStatus enum using Zod
export const CallStatusEnum = z.enum([
  "pending_scheduling",
  "scheduled",
  "scheduling_failed",
  "oauth_failed",
  "in_progress",
  "processed",
  "failed",
  "cancelled",
]);
export type CallStatus = z.infer<typeof CallStatusEnum>;

export const CallSchema = z.object({
  id: z.string(),
  orgId: z.string().optional(), // Making optional for now if not always present from all sources
  userId: z.string().optional(), // Making optional for now
  meetingUrl: z.string().nullable(), // Renamed from url, made nullable
  recallBotId: z.string().nullable(), // Made nullable
  deduplicationKey: z.string().nullable(), // Store the last used deduplication key
  callScriptId: z.string().optional().nullable(), // Kept optional, made nullable
  status: CallStatusEnum, // Use the Zod enum for status
  summary: z.string().nullable(), // Added summary
  startTime: z.string(), // Added startTime (assuming it's a string like ISO date)
  endTime: z.string().nullable(), // Added endTime, assuming it can be null if call not ended
  originatingCalendarEventId: z.string().nullable(), // Added
  // recallData: z.record(z.unknown()).nullable(), // Consider if this is needed in shared, often large
  createdAt: z.string(), // Kept createdAt
  updatedAt: z.string().optional(), // Added updatedAt, making optional
});

export type Call = z.infer<typeof CallSchema>;

// The old type as used in the front end
export interface PlaybookItem {
  id: string;
  text: string;
  content?: string;
  completed: boolean;
}

export const PlaybookScriptItemSchema = z.object({
  id: z.string(),
  text: z.string(),
  completed: z.boolean(),
  completedAt: z.number().optional(),
  startedAt: z.number().optional(),
  partiallyCompleted: z.boolean().optional(),
  variant: z.enum(["script", "drill_down"]).optional(), // Eventually make this non-optional
});

export type PlaybookScriptItem = z.infer<typeof PlaybookScriptItemSchema>;

export const PlaybookSectionSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.enum(["pending", "in_progress", "complete"]),
  startTime: z.number().optional(),
  endTime: z.number().optional(),
  successCriteria: z.string(),
  instructions: z.string(),
  startTranscriptId: z.string().optional(),
  endTranscriptId: z.string().optional(),
  scriptItems: z.array(PlaybookScriptItemSchema),
  currentScriptItemId: z.string().nullable(),
});

export const PlaybookProgressStatusSchema = z.enum([
  "pending",
  "in_progress",
  "complete",
]);

export const PlaybookProgressSchema = z.object({
  sections: z.array(PlaybookSectionSchema),
  currentSectionId: z.string().nullable(),
  currentScriptItemId: z.string().nullable().optional(),
  status: PlaybookProgressStatusSchema,
});

export type PlaybookSection = z.infer<typeof PlaybookSectionSchema>;
export type PlaybookProgress = z.infer<typeof PlaybookProgressSchema>;
export type PlaybookProgressStatus = z.infer<
  typeof PlaybookProgressStatusSchema
>;
export const WebhookTranscriptUpdateSchema = z.object({
  original_transcript_id: z.number().optional(),
  speaker: z.string(),
  speaker_id: z.number(),
  words: z
    .array(
      z.object({
        text: z.string(),
        start_time: z.number(),
        end_time: z.number(),
      }),
    )
    .min(1),
  is_final: z.boolean(),
  language: z.string().nullable().optional(),
  source: z.string().nullable().optional(),
});

export type WebhookTranscriptUpdate = z.infer<
  typeof WebhookTranscriptUpdateSchema
>;

export const TranscriptWordSchema = z.object({
  text: z.string(),
  start_time: z.number(),
  end_time: z.number(),
});

export const TranscriptEventSchema = z.object({
  event: z.literal("bot.transcription"),
  data: z.object({
    bot_id: z.string(),
    recording_id: z.string(),
    transcript: WebhookTranscriptUpdateSchema,
  }),
});

export type TranscriptEvent = z.infer<typeof TranscriptEventSchema>;

export const StatusCodeSchema = z.enum([
  "ready",
  "joining_call",
  "in_waiting_room",
  "participant_in_waiting_room",
  "in_call_not_recording",
  "recording_permission_allowed",
  "recording_permission_denied",
  "in_call_recording",
  "call_ended",
  "recording_done",
  "done",
  "fatal",
  "analysis_done",
  "analysis_failed",
  "media_expired",
  "starting_up",
]);

export type StatusCode = z.infer<typeof StatusCodeSchema>;

export const SubCodeSchema = z.string().nullable();

export type SubCode = z.infer<typeof SubCodeSchema>;

export const MeetingMetadataSchema = z.record(z.string().nullable()).nullable();

// Teams Meeting URL schema
// "meeting_url": {
//   "meeting_id": null,
//   "meeting_password": null,
//   "organizer_id": "db4c998f-101c-480d-9675-157c9d2819d4",
//   "tenant_id": "43c30cec-3754-472b-b062-99297f0412df",
//   "message_id": "0",
//   "thread_id": "19:meeting_MzJlZTIyODgtMDZmOS00Yzg2LTgwY2YtMWZmMDU4MGZiZGI5@thread.v2",
//   "business_meeting_id": null,
//   "business_meeting_password": null,
//   "platform": "microsoft_teams"
// },

// Google Meet Meeting URL schema
// "meeting_url": {
//   "meeting_id": "abc-xyz",
//   "platform": "google_meet"
// },

// Zoom Meet Meeting URL schema
// "meeting_url": {
//   "meeting_id": "abc-xyz",
//   "platform": "zoom"
// },

export const MicrosoftTeamsUrlSchema = z
  .object({
    meeting_id: z.string().optional().nullable(),
    meeting_password: z.string().optional().nullable(),
    organizer_id: z.string().optional().nullable(),
    tenant_id: z.string().optional().nullable(),
    message_id: z.string().optional().nullable(),
    thread_id: z.string().optional().nullable(),
    business_meeting_id: z.string().optional().nullable(),
    business_meeting_password: z.string().optional().nullable(),
    platform: z.literal("microsoft_teams"),
  })
  .passthrough();

export const MicrosoftTeamsLiveUrlSchema = z
  .object({
    meeting_id: z.string().optional().nullable(),
    meeting_password: z.string().optional().nullable(),
    organizer_id: z.string().optional().nullable(),
    tenant_id: z.string().optional().nullable(),
    message_id: z.string().optional().nullable(),
    thread_id: z.string().optional().nullable(),
    business_meeting_id: z.string().optional().nullable(),
    business_meeting_password: z.string().optional().nullable(),
    platform: z.literal("microsoft_teams_live"),
  })
  .passthrough();

export type MicrosoftTeamsUrl = z.infer<typeof MicrosoftTeamsUrlSchema>;
export type MicrosoftTeamsLiveUrl = z.infer<typeof MicrosoftTeamsLiveUrlSchema>;

export const MeetingUrlSchema = z.discriminatedUnion("platform", [
  z
    .object({
      meeting_id: z.string(),
      platform: z.literal("google_meet"),
    })
    .passthrough(),
  z
    .object({
      meeting_id: z.string(),
      platform: z.literal("zoom"),
    })
    .passthrough(),
  MicrosoftTeamsUrlSchema,
  MicrosoftTeamsLiveUrlSchema,
]);

export type MeetingUrl = z.infer<typeof MeetingUrlSchema>;

// Status change schema
export const StatusChangeSchema = z.object({
  code: StatusCodeSchema,
  created_at: z.string(),
  sub_code: SubCodeSchema.nullable().optional(),
  message: z.string().nullable().optional(),
  recording_id: z.string().optional(),
});

export type StatusChange = z.infer<typeof StatusChangeSchema>;

// Status change event schema
export const StatusChangeEventSchema = z.object({
  event: z.literal("bot.status_change"),
  data: z.object({
    bot_id: z.string(),
    status: StatusChangeSchema,
  }),
});

export type StatusChangeEvent = z.infer<typeof StatusChangeEventSchema>;

// Bot log event schema
export const BotLogEventSchema = z.object({
  event: z.enum(["bot.log", "bot.output_log"]),
  data: z.object({
    bot_id: z.string().optional(),
    job_id: z.string().optional(),
    log: z.object({
      level: z.string(),
      message: z.string(),
      output_id: z.string().nullable(),
      created_at: z.string(),
    }),
  }),
});

export type BotLogEvent = z.infer<typeof BotLogEventSchema>;

export const ParticipantInWaitingRoomEventSchema = z.object({
  event: z.literal("bot.participant_in_waiting_room"),
  data: z.object({
    bot_id: z.string(),
    participant_id: z.number(),
    created_at: z.string(),
  }),
});

export type ParticipantInWaitingRoomEvent = z.infer<
  typeof ParticipantInWaitingRoomEventSchema
>;

// Participant Join Event
export const ParticipantJoinEventSchema = z.object({
  event: z.literal("bot.participant_join"),
  data: z.object({
    bot_id: z.string(),
    participant_id: z.number(),
    created_at: z.string(),
  }),
});

export type ParticipantJoinEvent = z.infer<typeof ParticipantJoinEventSchema>;

// Participant Leave Event
export const ParticipantLeaveEventSchema = z.object({
  event: z.literal("bot.participant_leave"),
  data: z.object({
    bot_id: z.string(),
    participant_id: z.number(),
    created_at: z.string(),
  }),
});

export type ParticipantLeaveEvent = z.infer<typeof ParticipantLeaveEventSchema>;

// Active Speaker Notify Event
export const ActiveSpeakerNotifyEventSchema = z.object({
  event: z.literal("bot.active_speaker_notify"),
  data: z.object({
    bot_id: z.string(),
    participant_id: z.number(),
    created_at: z.string(),
  }),
});

export type ActiveSpeakerNotifyEvent = z.infer<
  typeof ActiveSpeakerNotifyEventSchema
>;

// Combined WebhookEvent
export const WebhookEventSchema = z.discriminatedUnion("event", [
  BotLogEventSchema,
  TranscriptEventSchema,
  StatusChangeEventSchema,
  ParticipantJoinEventSchema,
  ParticipantLeaveEventSchema,
  ParticipantInWaitingRoomEventSchema,
  ActiveSpeakerNotifyEventSchema,
]);

// Call Event (combination of participant and active speaker events)
export type CallEvent =
  | ParticipantJoinEvent
  | ParticipantLeaveEvent
  | ActiveSpeakerNotifyEvent;
export type WebhookEvent = z.infer<typeof WebhookEventSchema>;
export type TranscriptWord = z.infer<typeof TranscriptWordSchema>;

// Participant event schema
export const ParticipantEventSchema = z.object({
  code: z.string(),
  created_at: z.string(),
});

// Extra data schemas
export const ZoomExtraDataSchema = z.object({
  user_guid: z.string(),
  guest: z.boolean(),
  conf_user_id: z.string(),
});

export const MicrosoftTeamsExtraDataSchema = z.object({
  participant_type: z.string(),
  role: z.string(),
  meeting_role: z.string(),
  user_id: z.string(),
  tenant_id: z.string(),
  client_version: z.string(),
});

export const SlackExtraDataSchema = z.object({
  user_id: z.string(),
  email: z.string(),
});

export const ExtraDataSchema = z.object({
  zoom: ZoomExtraDataSchema.optional(),
  microsoft_teams: MicrosoftTeamsExtraDataSchema.optional(),
  slack: SlackExtraDataSchema.optional(),
});

export const ParticipantRoleSchema = z.enum([
  "salesperson",
  "client",
  "observer",
]);
export type ParticipantRole = z.infer<typeof ParticipantRoleSchema>;

// Meeting participant schema
export const MeetingParticipantSchema = z.object({
  id: z.number(),
  name: z.string(),
  events: z.array(ParticipantEventSchema),
  is_host: z.boolean(),
  platform: z.string(),
  extra_data: ExtraDataSchema.nullable().optional(),
  role: ParticipantRoleSchema.optional(),
});

export type MeetingParticipant = z.infer<typeof MeetingParticipantSchema>;

// Calendar meeting schema
export const CalendarMeetingSchema = z.object({
  id: z.string(),
  start_time: z.string(),
  end_time: z.string(),
  calendar_user: z.object({
    id: z.string(),
    external_id: z.string(),
  }),
});

// Recording schema
export const RecordingSchema = z.object({
  id: z.string(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
});

// Bot result schema
export const RecallBotSchema = z.object({
  id: z.string(),
  video_url: z.string().nullable().optional().nullable(),
  media_retention_end: z.string().nullable().optional().nullable(),
  status_changes: z.array(StatusChangeSchema).optional().nullable(),
  meeting_metadata: MeetingMetadataSchema.optional().nullable(),
  meeting_participants: z.array(MeetingParticipantSchema).optional().nullable(),
  meeting_url: MeetingUrlSchema.optional().nullable(),
  metadata: z.record(z.unknown()).optional().nullable(),
  join_at: z.string().nullable().optional().nullable(),
  calendar_meetings: z.array(CalendarMeetingSchema).optional().nullable(),
  recording: z.string().nullable().optional().nullable(),
  recordings: z.array(RecordingSchema).optional().nullable(),
});

// GET bot response schema
export const GetBotResponseSchema = z.object({
  count: z.number(),
  next: z.string().nullable(),
  previous: z.string().nullable(),
  results: z.array(RecallBotSchema),
});

// CREATE bot response schema
export const CreateBotResponseSchema = RecallBotSchema;

export type GetBotResponse = z.infer<typeof GetBotResponseSchema>;
export type CreateBotResponse = z.infer<typeof CreateBotResponseSchema>;
export type RecallBot = z.infer<typeof RecallBotSchema>;

export const AIQuestionAnswerSchema = z.object({
  id: z.string(),
  question: z.string(),
  answer: z.string(),
  context: z.string(),
  timestamp: z.number(),
  isComplete: z.boolean().optional(),
  questionId: z.string().optional(),
});
export type AIQuestionAnswer = z.infer<typeof AIQuestionAnswerSchema>;

export const PartialAIResponseSchema = z.object({
  id: z.string().nullable().optional(),
  question: z.string().nullable().optional(),
  answer: z.string().nullable().optional(),
  context: z.string().nullable().optional(),
  isComplete: z.boolean().nullable().optional(),
  questionId: z.string().nullable().optional(),
});

export type PartialAIResponse = z.infer<typeof PartialAIResponseSchema>;

export const AILatencyMetricsSchema = z.object({
  clickTimestamp: z.number(),
  aiRequestTimestamp: z.number(),
  firstTokenTimestamp: z.number(),
  completionTimestamp: z.number(),
  totalDuration: z.number(),
  aiRequestDuration: z.number(),
  generationDuration: z.number(),
  model: z.string(),
  error: z.boolean().optional(),
  questionId: z.string().optional(),
});
export type AILatencyMetrics = z.infer<typeof AILatencyMetricsSchema>;

export const KnowledgeMatchSchema = z.object({
  text: z.string(),
  fileName: z.string(),
  score: z.number(),
  chunkIndex: z.number().optional(),
  metadata: z.record(z.unknown()).optional(),
});

export type KnowledgeMatch = z.infer<typeof KnowledgeMatchSchema>;

export const AIStreamingStatusSchema = z.enum([
  "started",
  "detecting_question",
  "searching_faqs",
  "found_faq",
  "searching_knowledge",
  "has_knowledge",
  "no_knowledge",
  "generating_answer",
  "streaming",
  "done",
]);

export type AIStreamingStatus = z.infer<typeof AIStreamingStatusSchema>;
export const CallQuestionSchema = z.object({
  id: z.string(),
  question: z.string(),
  context: z.string(),
  timestamp: z.number(),
  isAnswered: z.boolean(),
  speakerId: z.number(),
  isFinalized: z.boolean(),
});

export type CallQuestion = z.infer<typeof CallQuestionSchema>;

export const InternalRequestSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("SET_CALL_SCRIPT"),
    payload: CallScriptSchema,
    isInternalCall: z.literal(true),
    timestamp: z.string().datetime({ precision: 3 }),
  }),
  z.object({
    type: z.literal("INIT"),
    payload: z.object({
      bot: RecallBotSchema,
      orgId: z.string(),
      callScript: CallScriptSchema,
    }),
    isInternalCall: z.literal(true),
    timestamp: z.string().datetime({ precision: 3 }),
  }),
  z.object({
    type: z.literal("AI_SCRIPT_COMPLETION"),
    payload: z.object({
      scriptItemId: z.string(),
      completionDecision: z.object({
        completed: z.boolean(),
        confidence: z.number(),
      }),
    }),
    isInternalCall: z.literal(true),
    timestamp: z.string().datetime({ precision: 3 }),
  }),
]);

export type InternalRequest = z.infer<typeof InternalRequestSchema>;

export interface LogMessage {
  message: string;
  timestamp: string;
}
